import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import _xor from 'lodash/xor';
import _get from 'lodash/get';
import qs from 'qs';

import intl from '../../shared/intl';
import endpoints from '../../../config/endpoints';
import http from '../../shared/http';
import createCalendar, {getDateRangeFromCalendar} from '../../shared/calendar';
import createFilters from '../../shared/filters';
import getRequestQuery from '../shared/get-request-query';
import checkPermissions from '../../shared/check-permissions';
import getFilterContext from '../shared/get-filters-context';
import isEmpty from 'lodash/isEmpty';
import csvExport from '../../shared/csvExport';

const calendar = createCalendar({
	namespace: 'unacceptableConversionsStat',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(6, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'unacceptableConversionsStat',
	stateNodeName: 'filters',
	needTakeInitialFiltersFromQuery: true,
	queryBuildFunc: getRequestQuery,
	listOfFilters: [{
		name: 'affiliates',
		title: 'Affiliates',
		placeholder: 'Add affiliates',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.AFFILIATES
		}
	}, {
		name: 'offers',
		title: 'Offers',
		placeholder: 'Add offers',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.OFFERS
		}
	}, {
		name: 'managers',
		title: 'Managers',
		placeholder: 'Add manager',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.ADMINS
		}
	}, {
		name: 'countries',
		title: 'Countries',
		placeholder: 'Add countries',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.LOCATION_CODES_WITH_TRAFFIC
		}
	}, {
		name: 'unacceptable_conversion_types',
		title: 'Unacceptable conversion types',
		placeholder: 'Enter types',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'static',
			source: 'root.lists.unacceptable-conversion-types'
		}
	}]
});

// actions types
const FETCH_DATA_SUCCESS = 'unacceptable-conversions-stat/fetch-data-success';
const FETCH_DATA_ERROR = 'unacceptable-conversions-stat/fetch-data-error';

const SHOW_PROGRESS_BAR = 'unacceptable-conversions-stat/show-progress-bar';
const CLEAR = 'unacceptable-conversions-stat/clear';

const TOGGLE_EXPANDED_AFFILIATES = 'unacceptable-conversions-stat/TOGGLE_EXPANDED_AFFILIATES';

// permissions
export const allowedPermissions = {
	'autotrash_view': 'autotrash_view',
	'resend': 'autotrash_resend'
};

// initial state
const initialState = {
	data: null,
	showProgressBar: false,
	calendar: calendar.initialState,
	filters: filters.initialState,
	expandedAffiliates: [],
	query: {
		date_range: {
			start: null,
			end: null
		}
	},
	error: false
};

// actions
export const actions = Object.assign({
	fetchData: () => (dispatch) => {
		dispatch(actions.fetchRequest());
	},
	fetchRequest: () => (dispatch, getState) => {
		const query = {};
		const { calendar, filters: { selectedFilters }, expandedAffiliates } = getState().unacceptableConversionsStat;
		const dateRange = getDateRangeFromCalendar(calendar);
		const filters = {};
		query['date_range'] = dateRange;
		query['expandedAffiliates'] = expandedAffiliates;

		if (selectedFilters) {
			selectedFilters.forEach(filter => {
				const values = filter.value.map(f => f.value);
				if (values.length) {
					filters[filter.name] = values;
				}
			});

			query['filters'] = !isEmpty(filters) ? filters : {};
		}

		return http({
			url: `${endpoints.UNACCEPTABLE_CONVERSIONS.BASE}?${qs.stringify(query)}`,
			method: 'get',
			actions: [
				FETCH_DATA_SUCCESS,
				FETCH_DATA_ERROR
			]
		})(dispatch);
	},

	apply: () => dispatch => {
		dispatch(actions.showProgressBar());
		dispatch(actions.fetchRequest());
	},
	exportCsv: () => (dispatch, getState) => {
		const headers = [ 'affiliate', 'offer_name', 'country_code' ];

		return actions.fetchRequest()(dispatch, getState)
			.then(action => {
				const rows = _get(action, 'payload.data.data', []);

				if (action.type === FETCH_DATA_SUCCESS && rows.length) {
					const cols = _get(action, 'payload.data.columns');
					headers.push(...cols);

					const columns = rows.map(entity => headers.map(index =>
						entity[index]
						|| (entity[`count_${index}`] && `${entity[`count_${index}`]}/${entity[`count_unique_${index}`]}`)
						|| '-'
					));

					const rowsForExport = [].concat([ headers ], columns);
					csvExport('Conversions_stat', rowsForExport);
				}
			});
	},

	toggleExpandedAffiliates: id => dispatch => {
		dispatch({ type: TOGGLE_EXPANDED_AFFILIATES, payload: id });
		dispatch(actions.fetchData());
	},

	clear: () => dispatch =>  {
		dispatch(replace('statistics/unacceptable-conversions-stat/'));
		dispatch({
			type: CLEAR
		});
	},

	showProgressBar: () => ({
		type: SHOW_PROGRESS_BAR
	})
},
calendar.actions,
filters.actions
);

// reducers
const reducers = Object.assign({
	[LOCATION_CHANGE]: () => {
		return initialState;
	},
	[FETCH_DATA_SUCCESS]: (state, action) => ({
		...state,
		...action.payload.data,
		showProgressBar: false, error: false
	}),
	[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
	[SHOW_PROGRESS_BAR]: state => ({...state, showProgressBar: true}),
	[SHOW_PROGRESS_BAR]: state => ({...state, showProgressBar: true}),
	[TOGGLE_EXPANDED_AFFILIATES]: (state, action) =>
		({...state, expandedAffiliates: [..._xor(state.expandedAffiliates, [ action.payload ])]}),
	[CLEAR]: state => ({
		...initialState,
		query: {
			...initialState.query,
			currency: state.query.currency
		}
	})
},
calendar.reducers,
filters.reducers
);
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.unacceptableConversionsStat.data,
	getCheckedBan: state => state.unacceptableConversionsStat.checkedBan,
	getIsDailyApprovePopupShowed: state => state.unacceptableConversionsStat.isDailyApprovePopupShowed,
	getWhitelistedIps: state => state.unacceptableConversionsStat.whitelistedIps,
	getBlacklistedIps: state => state.unacceptableConversionsStat.blacklistedIps,
	getIsBlacklistedIpsPopupShowed: state => state.unacceptableConversionsStat.isBlacklistedIpsPopupShowed,
	getIntl: intl,
	getTable: state => {
		return {
			rows: state.unacceptableConversionsStat.data,
			headers: state.unacceptableConversionsStat.columns
		};
	},
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.unacceptableConversionsStat.calendar,
		state => state
	)(state),
	getFilters: state => filters.selectors.getFilters(
		state => state.unacceptableConversionsStat.filters,
		state => state
	)(state),
	getProgressBar: state => state.unacceptableConversionsStat.showProgressBar,
	getSearchInitialValues: state => ({
		search: state.unacceptableConversionsStat.searchRefererText
	}),
	getSearchCrmIdInitialValues: state => ({
		search: state.unacceptableConversionsStat.searchCrmIdText
	}),
	getFilterContext: state => getFilterContext(state.unacceptableConversionsStat),
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.unacceptableConversionsStat.error
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/statistics/unacceptable-conversions-stat/index.js